import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "../hooks/useAuth";
import { useDispatch, useStore } from "react-redux";
import { api as rtkApi } from "../redux/api";
import { createSocketClient } from "../utils/socketAuth";
import { patchChatCaches } from "../redux/services/chat/cacheUtils";

const noop = () => {};

const fallbackChatContextValue = {
  socket: null,
  isConnected: false,
  joinConversation: noop,
  leaveConversation: noop,
  pendingConversationId: null,
  openConversationById: noop,
  clearPendingConversation: noop,
};

const ChatContext = createContext(fallbackChatContextValue);

/**
 * ChatProvider - Manages chat socket connections and provides chat-related state/functions
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export const ChatProvider = ({ children }) => {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const store = useStore();
  const [socket, setSocket] = useState(null);
  
  // State for opening a specific conversation from outside ChatWidget (e.g., from notifications)
  const [pendingConversationId, setPendingConversationId] = useState(null);

  useEffect(() => {
    if (!user) return;
    const newSocket = createSocketClient();

    newSocket.on("chat:message:new", (payload) => {
      const conversationId = payload?.conversationId;
      if (conversationId && payload?.message) {
        patchChatCaches({
          dispatch,
          getState: store.getState,
          conversationId,
          conversation: payload?.conversation,
          message: payload?.message,
        });
        return;
      }

      dispatch(
        rtkApi.util.invalidateTags(
          conversationId
            ? [
                { type: "Chat", id: "LIST" },
                { type: "ChatMessages", id: conversationId },
              ]
            : [{ type: "Chat", id: "LIST" }]
        )
      );
    });

    newSocket.on("chat:conversation:updated", (payload) => {
      const conversationId = payload?.conversationId;
      if (conversationId) {
        patchChatCaches({
          dispatch,
          getState: store.getState,
          conversationId,
          conversation: payload?.conversation || {
            _id: conversationId,
            lastMessageAt: payload?.lastMessageAt,
            lastMessagePreview: payload?.lastMessagePreview,
            unreadBy: payload?.unreadBy,
            readStateByUser: payload?.readStateByUser,
          },
        });
        return;
      }

      dispatch(rtkApi.util.invalidateTags([{ type: "Chat", id: "LIST" }]));
    });

    newSocket.on("chat:read", (payload) => {
      const conversationId = payload?.conversationId;
      if (conversationId) {
        patchChatCaches({
          dispatch,
          getState: store.getState,
          conversationId,
          conversation: payload?.conversation || {
            _id: conversationId,
            unreadBy: payload?.unreadBy,
            readStateByUser: payload?.readStateByUser,
          },
        });
        return;
      }

      dispatch(rtkApi.util.invalidateTags([{ type: "Chat", id: "LIST" }]));
    });

    setSocket(newSocket);
    return () => newSocket.close();
  }, [user, dispatch, store]);

  /**
   * Request to open a specific conversation by ID
   * Used by notifications to open chat when clicked
   * @param {string} conversationId - The conversation ID to open
   */
  const openConversationById = useCallback((conversationId) => {
    if (conversationId) {
      setPendingConversationId(conversationId);
    }
  }, []);

  /**
   * Clear the pending conversation ID after ChatWidget has consumed it
   */
  const clearPendingConversation = useCallback(() => {
    setPendingConversationId(null);
  }, []);

  const value = {
    socket,
    isConnected: socket?.connected || false,
    joinConversation: (conversationId) =>
      socket?.emit("chat:join", { conversationId }),
    leaveConversation: (conversationId) =>
      socket?.emit("chat:leave", { conversationId }),
    // For opening conversations from outside ChatWidget (e.g., notifications)
    pendingConversationId,
    openConversationById,
    clearPendingConversation,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChatSocket = () => useContext(ChatContext) || fallbackChatContextValue;

